import { computed, ref } from 'vue';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';

import { Offer, PopularTitlesSorting, PresentationType, TitleFilter } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { TITLE_DETAIL_MODULE_TECHNICAL_NAME } from '@/enums/module-template';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowImpressionContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers';

import { OfferLocation } from '@/helpers/offer-helper';
import { useAppStore, useConstantStore, useLanguageStore } from '@/helpers/composables/useStores';
import { useBatchImpressionTracking } from '@/helpers/composables/useBatchImpressionTracking';

import {
	compositionIDSuffixByPlacement,
	QualityTVPlacements,
	getItsctParam,
	qualityTVProviderConfig,
	QTVPackageIds,
	getCompositionIdPrefix,
	PrimeConfig,
} from '@/features/buybox/helpers/quality-tv-data';

import { ClickoutUrl, handleClickoutUrl, isClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { useSimilarTitles } from '@/helpers/composables/useSimilarTitles';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useUserAgent } from '@/helpers/composables/useUserAgent';
import { useQuery } from '@/helpers/composables/useApollo';
import { GetQtvPopularTitlesDocument, GetQtvPopularTitlesQuery } from '@/pages/graphql/queries/GetPopularTitles.query';
import { FromRefOrGetter, WithRequired } from '@/helpers/types';
import Provider from '@/interfaces/provider';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ModalHelper } from '@/helpers/modal-helper';
import { usePPExperiments } from '@/components/experiments/PPExperiments';

export interface UseQualityTvOptions {
	packageId?: number;
	titlesShownCount: MaybeRefOrGetter<number>;
	sourceTitle?: MaybeRefOrGetter<TitleDetail>;
	trackingContexts?: MaybeRefOrGetter<SnowplowContext[]>;
	additionalContexts?: MaybeRefOrGetter<SnowplowContext[]>;
	placement?: QualityTVPlacements;
	sortBy?: SortQualityTv;
	imdbMinimum?: number;
	titleId?: string;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	titleCount?: number;
	clickoutCallBack?: (offer: Offer) => void;
}

export type SortQualityTv = 'imdb' | 'none';

export type QTVSimilarTitle = FromRefOrGetter<
	FromRefOrGetter<ReturnType<typeof useQualityTV>>['qualityTVTitles']
>[number];

function simpleHash(str: string) {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = (hash << 5) - hash + str.charCodeAt(i);
		hash |= 0; // Convert to 32bit integer
	}
	return Math.abs(hash).toString(36); // Convert to base36 string
}

function generateHashFromIDs(idList: string[]) {
	const idString = idList.join('-');
	let hash = simpleHash(idString);
	// Ensure the resulting string is exactly 6 characters long
	if (hash.length < 6) {
		// If shorter than 6, pad with 0s
		hash = hash.padEnd(6, '0');
	} else if (hash.length > 6) {
		// If longer than 6, trim to 6 characters
		hash = hash.slice(0, 6);
	}
	return hash;
}

/**
 * This composable retrieves  Quality TV titles and manages tracking logic.
 * It supports two versions of the QualityTV component, which have the same functionality but different UIs.
 */
export function useQualityTV({
	packageId = 350,
	titlesShownCount,
	sourceTitle = undefined,
	titleId = '',
	trackingContexts = [],
	additionalContexts = [],
	placement = 'TitlePage',
	sortBy = 'imdb',
	imdbMinimum = 7.5,
	seasonNumber = null,
	episodeNumber = null,
	titleCount = 8,
	clickoutCallBack = () => {},
}: UseQualityTvOptions) {
	type SimilarTitle = (typeof similarTitles)['value'][number];
	type QTVTitle = WithRequired<SimilarTitle, 'offers'>;

	const { allProvidersById } = useConstantStore();
	const { xServeTrack } = useAppStore();

	const provider: Provider = allProvidersById.value[packageId];

	const moduleContext = new SnowplowModuleContext('', -1, TITLE_DETAIL_MODULE_TECHNICAL_NAME.QUALITY_TV, '');
	const similarTitlesFilters: TitleFilter = {
		imdbScore: { min: imdbMinimum },
		packages: [provider.shortName],
	};

	// Tracks the visibility state and index of each title (ID as key)
	const titlesVisibilityState = ref<Record<string, { index: number; isVisible: boolean }>>({});

	const { country, language } = useLanguageStore();
	const { providersById } = useConstantStore();
	const { placement: clickoutPlacement } = getClickoutTrackingPlacement('qtv');

	const { getQTVCompositionId } = useQTVCompositionId();

	const { setPPExperimentTags } = usePPExperiments('qtv');

	const _title = computed(() => toValue(sourceTitle));

	const _titleId = computed(() => (!!titleId ? titleId : _title.value?.id));

	const isSimilarTitlesQuery = ref(true);

	// Used to generate a unique composition ID from the visible titles IDs
	const compositionID = computed(() => {
		const titleVisibleIDs = getVisibleTitlesIDs();
		const hashID = generateHashFromIDs(titleVisibleIDs);
		return getTitleCompositionID(hashID);
	});

	const intersectionVariables = computed(() => ({
		additionalContexts: () => [getRecommenderContext(compositionID.value), moduleContext],
		additionalProperties: () => ({ value: Math.min(toValue(titlesShownCount), qualityTVTitles.value.length) }),
		preventDuplicateAdditionalContext: true,
	}));

	const { onIntersection } = useBatchImpressionTracking<QTVTitle & { index: number }>({
		category: 'recommender_feature',
		toContext: getTitleContext,
		...intersectionVariables.value,
	});

	const { onIntersection: onInterstectionOfferImpression } = useBatchImpressionTracking<QTVTitle & { index: number }>(
		{
			category: 'offer',
			toContext: getClickoutContext,
			...intersectionVariables.value,
		}
	);

	const movieOrShow = computed(() => {
		if (!_title.value) return null;
		const isSeason = _title.value?.__typename === 'Season' && !!_title.value.show;
		return isSeason ? _title.value.show! : _title.value;
	});

	const result = computed(() => {
		if (_title.value?.id != null || !!titleId) {
			const id = !!titleId ? titleId : () => movieOrShow.value!.id;
			const data = useSimilarTitles({
				titleId: id,
				filters: similarTitlesFilters,
				offerFilters: { packages: [provider.shortName] },
				includeOffers: true,
				first: titleCount,
			});
			if (data.loading.value || data.similarTitles.value.length > 0) return data;
		}

		isSimilarTitlesQuery.value = false;
		return usePopularTitles(country.value, language.value, [provider.shortName], titleCount);
	});

	const similarTitles = computed(() => result.value.similarTitles.value);
	const loading = computed(() => result.value.loading.value);

	const qualityTVTitles = computed(() => {
		const titles = similarTitles.value.filter(title => (title.offers?.length ?? 0) > 0);

		if (sortBy === 'imdb') {
			titles.sort((a, b) => (b.content.scoring.imdbScore ?? 0) - (a.content.scoring.imdbScore ?? 0));
		}

		return titles;
	});

	const params = computed(() => {
		return getItsctParam(country.value, placement);
	});

	function onVisibilityChanged(title: QTVTitle & { index: number }, isVisible: boolean) {
		titlesVisibilityState.value = { ...titlesVisibilityState.value, [title.id]: { isVisible, index: title.index } };
		onIntersection(title, isVisible);
		onInterstectionOfferImpression(title, isVisible, [getTitleContext(title)]);
	}

	function baseTrackingContexts(qtvTitle: QTVTitle & { index: number }) {
		// no clickout conext
		const compositionID = getTitleCompositionID(qtvTitle.id);
		const contexts: SnowplowContext[] = [
			getTitleContext(qtvTitle),
			getRecommenderContext(compositionID),
			moduleContext,
			...(toValue(additionalContexts) ? toValue(additionalContexts) : []),
		];

		return contexts;
	}

	function posterTrackingContexts(qtvTitle: QTVTitle & { index: number }) {
		// all contexts for tracking
		const baseContexts = baseTrackingContexts(qtvTitle);
		const clickoutContext: SnowplowContext[] = [
			SnowplowClickoutContextGraphql.fromProviderOffer(
				qtvTitle.offers[0],
				undefined,
				undefined,
				undefined,
				clickoutPlacement,
				'qtv'
			),
		];
		const contexts = baseContexts.concat(clickoutContext);

		return contexts;
	}

	function handleTitleClick(qtvTitle: QTVTitle & { index: number }, trackClickoutEvt: boolean = true) {
		const url = new ClickoutUrl(qtvTitle.offers[0], true)
			.addContexts(getTitleContext(qtvTitle))
			.setTitleSourceId(qtvTitle.id)
			.skipYieldOptimization()
			.updateContext('ClickoutContextGraphql', 'placement', clickoutPlacement)
			.updateContext('ClickoutContextGraphql', 'clickoutType', 'qtv')
			.skipUCT2()
			.setCanary(xServeTrack.value);

		if (trackClickoutEvt) {
			url.apply(qualityTvProperties).addContexts(posterTrackingContexts(qtvTitle));
			trackFreeTrialClick(qtvTitle.offers[0]);
		}

		if (packageId === 2285) {
			openJWTVModal(qtvTitle, url);
			return;
		}

		const ctParam = getTitleCompositionID(qtvTitle.id);

		if (packageId === 350) {
			url.set('uct_ct', ctParam)
				.set('uct_itsct', params.value)
				.setUCTHijackParams({ titleIdOrHash: _titleId.value ?? 'nsid' });
		} else if (packageId === 2) {
			const ctParam = `appletv-qtv`;
			const itsctParam = `justwatch_tv_7`;

			url.set('uct_ct', ctParam).set('uct_itsct', itsctParam);
		} else if (packageId === 9 || packageId === 119) {
			const primeConfig = getPrimeConfig();
			const tag = primeConfig?.posterTag;
			!!primeConfig?.isImpact
				? url.setOnOffer('subId3', tag)
				: url.setOnOffer('tag', tag).set('uct_ct', 'quality-tv');

			url.apply(url => setPPExperimentTags(url, 'pos'));

			url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
		} else if ([158, 122, 1773, 337].includes(packageId)) {
			url.set('uct_ct', 'quality-tv');
		}

		handleClickoutUrl(url, false, true);
	}

	const compositionIdPrefix = computed(() => getCompositionIdPrefix(packageId) || provider.shortName);

	function getTitleCompositionID(id: string) {
		const sourceTitleID = _titleId.value ?? 'nsid';
		const suffix = placement === 'TitlePage' ? null : compositionIDSuffixByPlacement[placement];
		return getQTVCompositionId(compositionIdPrefix.value, sourceTitleID, id, suffix);
	}

	const fallbackUrlContext = (u: ClickoutUrl) => {
		const baseTitleContext = _title.value
			? new SnowplowTitleContextGraphql(
					_title.value.__typename === 'Season' ? _title.value.show.objectId : _title.value.objectId,
					_title.value.__typename === 'Season' ? _title.value.show.objectType : _title.value.objectType,
					seasonNumber,
					episodeNumber
			  )
			: null;
		return u
			.setTitleSourceId(_title.value?.id ?? null)
			.addContexts(getRecommenderContext(compositionID.value))
			.addContexts(moduleContext)
			.addContexts(baseTitleContext)
			.updateContext('ClickoutContextGraphql', 'placement', clickoutPlacement)
			.updateContext('ClickoutContextGraphql', 'clickoutType', 'qtv')
			.setCanary(xServeTrack.value);
	};

	function getFallbackUrl(packageId: QTVPackageIds) {
		if (packageId === 350) {
			const _package = providersById.value[packageId] as Provider;
			const ctParam = compositionID.value;
			const itsctParam = params.value;

			return new ClickoutUrl(
				{
					monetizationType: _package.monetizationTypes[0],
					presentationType: PresentationType['Sd'],
					package: { ..._package },
					standardWebURL: `https://tv.apple.com/channel/tvs.sbd.4000?at=1000l3V2&ct=${ctParam}&itscg=30200&itsct=${itsctParam}`,
				},
				true
			)
				.apply(fallbackUrlContext)
				.skipRedirect();
		}

		if (packageId === 2) {
			const ctParam = `appletv-qtv`;
			const itsctParam = `justwatch_tv_7`;
			// for apple tv, we don't have a homepago to clickout to, so we clickout to first title in the similar titles.
			return new ClickoutUrl(qualityTVTitles.value[0].offers[0], true)
				.apply(fallbackUrlContext)
				.skipYieldOptimization()
				.skipUCT2()
				.set('uct_country', country.value)
				.set('uct_ct', ctParam)
				.set('uct_itsct', itsctParam);
		}

		if ([158, 122, 1773, 337, 2285].includes(packageId)) {
			// SVOD titles will also point to first title
			return new ClickoutUrl(qualityTVTitles.value[0].offers[0], true)
				.apply(fallbackUrlContext)
				.skipYieldOptimization()
				.skipUCT2()
				.set('uct_country', country.value)
				.set('uct_ct', 'quality-tv-cta');
		}

		if (packageId === 9 || packageId === 119) {
			const primeConfig = getPrimeConfig();
			const url = primeConfig?.freeTrialUrl ?? (qualityTVProviderConfig[packageId] as PrimeConfig).default;

			const offer = {
				id: 'PROMOTION_OFFER',
				monetizationType: 'free',
				presentationType: 'hd',
				package: {
					clearName: provider.clearName,
					packageId,
				},
				standardWebURL: url,
			};
			const ctParam = !!primeConfig?.isImpact ? 'bypass' : 'fallback';

			return new ClickoutUrl(offer, true)
				.apply(fallbackUrlContext)
				.skipYieldOptimization()
				.skipUCT2()
				.set('uct_ct', ctParam)
				.set('uct_country', country.value)
				.apply(url => setPPExperimentTags(url, 'cta'));
		}

		// if it's null, then the clickout will be treated as a title clickout with handleTitleClick.
		return null;
	}

	function getVisibleTitlesIDs() {
		return Object.keys(titlesVisibilityState.value)
			.filter(key => titlesVisibilityState.value[key].isVisible)
			.sort((a, b) => titlesVisibilityState.value[a].index - titlesVisibilityState.value[b].index);
	}

	function qualityTvProperties(u: ClickoutUrl) {
		return u
			.addContexts(getRecommenderContext(compositionID.value))
			.addContexts(getImpressionContext())
			.addContexts(moduleContext)
			.addContexts(...(toValue(trackingContexts) ? toValue(trackingContexts) : []))
			.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
			.set('uct_country', country.value)
			.updateContext('ClickoutContextGraphql', 'placement', clickoutPlacement)
			.updateContext('ClickoutContextGraphql', 'clickoutType', 'qtv')
			.skipUCT2()
			.setCanary(xServeTrack.value);
	}

	async function handleFreeTrialClick() {
		trackFreeTrialClick(null);

		const fallbackUrl = getFallbackUrl(packageId);

		if (packageId === 2285) {
			openJWTVModal(qualityTVTitles.value[0], fallbackUrl);

			return;
		}

		if (fallbackUrl) {
			if (isClickoutUrl(fallbackUrl)) fallbackUrl.apply(qualityTvProperties);
			handleClickoutUrl(fallbackUrl);
		} else {
			// if the fallback url isn't set for that provider, we'll click out to the first title in the similar titles.
			return handleTitleClick({ ...qualityTVTitles.value[0], index: -1 });
		}
	}

	function trackFreeTrialClick(offer: Partial<NonNullable<QTVTitle['offers']>[number]> | null) {
		clickoutCallBack(offer as Offer);
	}

	function getImpressionContext() {
		const visibleTitles = getVisibleTitlesIDs();
		return new SnowplowImpressionContext(visibleTitles.length, 0, visibleTitles);
	}

	function getRecommenderContext(compositionID: string | null = null) {
		let _titleId = 'nsid';
		let source_season_number = null;
		if (!!_title.value) {
			source_season_number = _title.value.__typename === 'Season' ? _title.value.content.seasonNumber : null;
			_titleId = _title.value.__typename === 'Season' ? _title.value.show?.id : _title.value.id;
		}

		if (!!titleId) {
			_titleId = titleId;
		}

		return TrackingHelper.getRecommenderContext('quality_tv', _titleId, source_season_number, null, compositionID);
	}

	function getTitleContext(qtvTitle: QTVTitle & { index: number }) {
		return new SnowplowTitleContextGraphql(
			qtvTitle.objectId,
			qtvTitle.objectType,
			undefined,
			null,
			undefined,
			qtvTitle.index
		);
	}
	function getClickoutContext(qtvTitle: QTVTitle & { index: number }) {
		return SnowplowClickoutContextGraphql.fromProviderOffer(
			qtvTitle.offers[0],
			undefined,
			undefined,
			undefined,
			clickoutPlacement,
			'qtv'
		);
	}

	function getPrimeConfig() {
		const isv1 =
			placement.includes('V1') &&
			(qualityTVProviderConfig[packageId] as PrimeConfig)['urls']['V1']?.[country.value];
		return isv1
			? (qualityTVProviderConfig[packageId] as PrimeConfig)['urls']['V1']?.[country.value]
			: (qualityTVProviderConfig[packageId] as PrimeConfig)['urls']['DEFAULT']?.[country.value];
	}

	function openJWTVModal(qtvTitle: QTVTitle, url: ClickoutUrl | null) {
		ModalHelper.openModal(
			() => import('@/ui-components/JwTVModal.vue'),
			{ offer: qtvTitle.offers[0], title: qtvTitle },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);

		if (url) trackClickout(url);
	}

	return {
		qualityTVTitles,
		loading,
		posterTrackingContexts,
		baseTrackingContexts,
		handleTitleClick,
		handleFreeTrialClick,
		onVisibilityChanged,
		isSimilarTitlesQuery,
	};
}

function usePopularTitles(country: string, language: string, packages: string[], first: number) {
	const { result, loading } = useQuery<GetQtvPopularTitlesQuery>(GetQtvPopularTitlesDocument, {
		country: country,
		popularTitlesSortBy: PopularTitlesSorting.Popular_7Days,
		first,
		popularTitlesFilter: { packages, imdbScore: { min: 8 } },
		language: language,
		offerFilters: { packages, preAffiliate: true },
	});

	//similar titles
	const similarTitles = computed(() => result.value?.popularTitles?.edges?.map(({ node }) => node) ?? []);

	return {
		similarTitles,
		loading,
	};
}

/** Align QTV compositionID for all QTV types - QTV, SQTV */

export function useQTVCompositionId() {
	const { deviceOs, deviceType } = useUserAgent();
	const deviceOsParam = computed(() => {
		return ['ios', 'macos'].includes(deviceOs.value) ? 'os' : deviceOs.value === 'android' ? 'and' : 'ot';
	});

	const deviceTypeParam = computed(() => (deviceType.value === 'mobile' ? 'mo' : 'cm'));
	const deviceParam = computed(() => `${deviceTypeParam.value}-${deviceOsParam.value}`);

	function getQTVCompositionId(
		prefix: string,
		titleId: string,
		/** For SQTV and QTV CTA, we pass the similar title id, else a hash of the similar titles */
		similarTitleIdOrHash: string,
		suffix?: string | null
	) {
		return [prefix, titleId, similarTitleIdOrHash, deviceParam.value, suffix].filter(Boolean).join('-');
	}

	return {
		getQTVCompositionId,
		deviceParam,
	};
}
